import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import CharterBundleCalendarModal from './CharterBundleCalendarModal'
import { FlightsClassTypes } from 'constants/flight'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  nights: number
  fareClass: FlightsClassTypes
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  checkInDate?: string
  charterBundleRoute?: App.CharterBundleRoute
  rooms?: Array<App.Occupants>
  className?: string
}

/**
 * Charter Flight Calendar Select for Desktop
 * Uses ModalContext pattern to open the calendar modal
 */
function CharterFlightCalendarSelect(props: Props) {
  const {
    selectedMonth,
    onMonthChange,
    nights,
    fareClass,
    onDurationChange,
    durationOptions,
    checkInDate,
    charterBundleRoute,
    rooms,
    className,
  } = props

  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()

  const handleFareClassChange = useCallback((selectedClass: FlightsClassTypes) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
  }, [dispatch])
  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Set default dates if checkInDate is not set
  useEffect(() => {
    if (!checkInDate) {
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: '2026-04-07',
        checkOut: '2026-04-12',
      })
    }
  }, [checkInDate, dispatch])

  const openCalendarModal = useCallback(async() => {
    const selectedDates = await openModal<{ departDate: string, returnDate: string, checkIn: string, checkOut: string } | undefined>(
      <CharterBundleCalendarModal
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        onDurationChange={onDurationChange}
        durationOptions={durationOptions}
        onFareClassChange={handleFareClassChange}
      />
    )

    if (selectedDates) {
      // Update dates in offer page state
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut,
      })
      dispatch({ type: OfferPageActions.closeFilter })
    }
  }, [
    openModal,
    dispatch,
    selectedMonth,
    onMonthChange,
    onDurationChange,
    durationOptions,
    handleFareClassChange,
  ])

  // Display selected date or placeholder
  // For charter bundle, display flight dates (departDate/returnDate) instead of hotel dates (checkIn/checkOut)
  const value = useMemo(() => {
    // Use flight dates if available (charter bundle mode), otherwise fall back to hotel dates
    if (departDate && returnDate) {
      const dateText = `${intlDateFormatter(departDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(returnDate, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> {nightsText}</>
    }
    if (checkInDate) {
      const checkOut = moment(checkInDate).add(nights, 'days').format('YYYY-MM-DD')
      const dateText = `${intlDateFormatter(checkInDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(checkOut, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> {nightsText}</>
    }
    return undefined
  }, [checkInDate, departDate, returnDate, nights, intlDateFormatter])

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="charter-calendar-select"
      onClick={openCalendarModal}
      icon={<LineCalendarIcon />}
      label="When"
      value={value}
      placeholder="Select your dates"
    />
  )
}

export default CharterFlightCalendarSelect
